// src/pages/packages/steps/PackageStep2.jsx
import "./PackageStep2.scss";

import { Col, Modal, Row } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppContainer from "../../../../../commons/containers/AppContainer";
import AppInputV2 from "../../../../../commons/inputs/AppInputV2";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import CategoryAPI from "../../../../services/CategoryAPI";
import CompanyAPI from "../../../../company/CompanyAPI";
import HallAppToast from "../../../../../commons/HallAppToast";
import { normalizeString } from "../../../../../commons/util/HallAppUtil";

const companyAPI = new CompanyAPI();
const categoryAPI = new CategoryAPI();

/**
 * Tipos de pacote
 */
const PACKAGE_ITEM_TYPES = [
  {
    value: "SERVICE_QUANTITY",
    label: "Sessões de um serviço",
    hint: "Ex: 10 sessões de Depilação Axila",
  },
  {
    value: "COMBO_SERVICES",
    label: "Combo de serviços",
    hint: "Ex: 3 Massagens + 1 Limpeza de Pele",
  },
];

/* =====================================================
   BottomSheet Service Picker
===================================================== */

function BottomSheetMultiServicePicker({
  show,
  onClose,
  categories,
  selectedIds,
  onConfirm,
  title,
  maxSelect,
}) {
  const [q, setQ] = useState("");
  const [tmp, setTmp] = useState([]);

  useEffect(() => {
    if (show) {
      setQ("");
      setTmp(selectedIds || []);
    }
  }, [show, selectedIds]);

  const filtered = useMemo(() => {
    const nq = normalizeString(q);

    if (!nq) return categories;

    return (categories || [])
      .map((c) => ({
        ...c,
        services: (c.services || []).filter((s) =>
          normalizeString(`${s.name} ${c.name}`).includes(nq)
        ),
      }))
      .filter((c) => c.services.length);
  }, [q, categories]);

  const toggle = (id) => {
    setTmp((prev) => {
      const exists = prev.includes(id);

      if (exists) return prev.filter((x) => x !== id);

      if (maxSelect && prev.length >= maxSelect) {
        return [id];
      }

      return [...prev, id];
    });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      fullscreen="md-down"
      centered
      contentClassName="hallapp-modal"
      dialogClassName="bottomsheet-modal"
    >
      <Modal.Header closeButton>
        <div className="w-100">
          <strong>{title}</strong>

          <div className="mt-2">
            <AppInputV2
              label="Pesquisar"
              name="q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              errors={{}}
              touched={{}}
            />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {(filtered || []).map((cat) => (
          <div key={cat.id} className="mb-3">
            <div style={{ fontSize: 12, fontWeight: 800 }}>{cat.name}</div>

            {(cat.services || []).map((s) => {
              const checked = tmp.includes(s.id);

              return (
                <button
                  key={s.id}
                  type="button"
                  className={`service-pick-row ${checked ? "active" : ""}`}
                  onClick={() => toggle(s.id)}
                >
                  <div
                    className={`service-pick-check ${
                      checked ? "checked" : ""
                    }`}
                  >
                    {checked ? "✓" : ""}
                  </div>

                  <div className="service-pick-name">
                    <strong>{s.name}</strong>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </Modal.Body>

      <div className="service-pick-footer">
        <AppLinkButton
          name="Confirmar"
          fullWidth
          onClick={() => {
            onConfirm(tmp);
            onClose();
          }}
        />
      </div>
    </Modal>
  );
}

/* =====================================================
   UI
===================================================== */

function TypeCard({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="package-step2__option-card w-100"
      data-active={active ? "1" : "0"}
    >
      <div className="package-step2__option-title">{item.label}</div>
      <div className="package-step2__option-hint">{item.hint}</div>
    </button>
  );
}

function PackageItemRow({ item, onQtyChange, onRemove }) {
  return (
    <div className="package-step2__item-row">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          <div className="package-step2__item-title">{item.serviceName}</div>

          <div className="package-step2__item-meta text-muted">
            Sessões: {item.quantity}
          </div>
        </div>

        <div className="d-flex gap-2">
          <div style={{ width: 120 }}>
            <AppInputV2
              label="Qtd"
              type="number"
              value={item.quantity}
              onChange={(e) => onQtyChange(item.serviceId, e.target.value)}
              errors={{}}
              touched={{}}
            />
          </div>

          <button
            type="button"
            className="package-step2__remove"
            onClick={() => onRemove(item.serviceId)}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   STEP 2
===================================================== */

function PackageStep2({ formik, next, toBack }) {
  const { values, setFieldValue } = formik;

  const items = values?.items || [];

  const [itemsType, setItemsType] = useState(
    values?.itemsType || "SERVICE_QUANTITY"
  );

  const [categories, setCategories] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  /* =====================
     Load services
  ===================== */

  useEffect(() => {
    (async () => {
      try {
        const resp = await categoryAPI.findAllByCompanyScheduler(
          companyAPI.getCompanyManagment().id
        );

        setCategories(resp.data || []);
      } catch {
        HallAppToast.genericError();
      }
    })();
  }, []);

  /* =====================
     Service lookup
  ===================== */

  const serviceLookup = useMemo(() => {
    const map = new Map();

    categories.forEach((c) => {
      (c.services || []).forEach((s) => {
        map.set(String(s.id), s);
      });
    });

    return map;
  }, [categories]);

  /* =====================
     Resolve names
  ===================== */

  const resolvedItems = useMemo(() => {
    return (items || []).map((it) => {
      const svc = serviceLookup.get(String(it.serviceId));

      return {
        ...it,
        serviceName:
          it.serviceName ||
          svc?.name ||
          `Serviço #${it.serviceId}`,
      };
    });
  }, [items, serviceLookup]);

  const selectedIds = resolvedItems.map((i) => i.serviceId);

  /* =====================
     Picker confirm
  ===================== */

  const onConfirmServices = (ids) => {
    const nextItems = ids.map((id) => {
      const svc = serviceLookup.get(String(id));

      return {
        serviceId: id,
        serviceName: svc?.name,
        quantity: 1,
      };
    });

    setFieldValue("items", nextItems);
  };

  /* =====================
     Qty
  ===================== */

  const onQtyChange = (serviceId, qty) => {
    const nextItems = items.map((it) =>
      it.serviceId === serviceId
        ? { ...it, quantity: Number(qty) }
        : it
    );

    setFieldValue("items", nextItems);
  };

  const removeItem = (serviceId) => {
    const nextItems = items.filter((it) => it.serviceId !== serviceId);
    setFieldValue("items", nextItems);
  };

  /* =====================
     Next
  ===================== */

  const goNext = () => {
    if (!items.length) {
      HallAppToast.error("Selecione pelo menos um serviço.");
      return;
    }

    next();
  };

  const pickerMax = itemsType === "SERVICE_QUANTITY" ? 1 : undefined;

  /* ===================================================== */

  return (
    <div className="package-step2" style={{ paddingBottom: 110 }}>
      <div className="mb-3">
        <div className="package-step2__title">Itens do pacote</div>
      </div>

      {/* Tipo */}
      <div className="package-step2__card">
        <Row className="g-2">
          {PACKAGE_ITEM_TYPES.map((t) => (
            <Col xs={12} md={6} key={t.value}>
              <TypeCard
                item={t}
                active={itemsType === t.value}
                onClick={() => {
                  setItemsType(t.value);
                  setFieldValue("itemsType", t.value);
                }}
              />
            </Col>
          ))}
        </Row>
      </div>

      {/* Serviços */}
      <div className="package-step2__card">
        <button
          className="package-step2__service-pick w-100"
          onClick={() => setPickerOpen(true)}
        >
          Selecionar serviços
        </button>

        <div className="d-flex flex-column gap-2 mt-3">
          {resolvedItems.map((it) => (
            <PackageItemRow
              key={it.serviceId}
              item={it}
              onQtyChange={onQtyChange}
              onRemove={removeItem}
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="package-step2__footer">
        <AppContainer>
          <AppLinkButton name="Próximo" fullWidth onClick={goNext} />
          <div style={{ marginTop: 10 }}>
            <AppButton name="Voltar" width="100%" onClick={toBack} />
          </div>
        </AppContainer>
      </div>

      <BottomSheetMultiServicePicker
        show={pickerOpen}
        onClose={() => setPickerOpen(false)}
        categories={categories}
        selectedIds={selectedIds}
        onConfirm={onConfirmServices}
        title="Selecionar serviços"
        maxSelect={pickerMax}
      />
    </div>
  );
}

export default PackageStep2;